import moment from "moment-timezone";
import "./date-time.less";
import template from "./date-time.html";
import { find } from "lodash";
import {
	EVENT_FILTER_PRIMARYDATE,
	EVENT_CLEAR_VALUE,
	EVENT_UNCLEAR_VALUE,
	EVENT_SET_VALUE,
	EVENT_SET_DEFAULT_VALUE,
	EVENT_VALUE_CHANGED,
	TIME_DISPLAY_OPTIONS,
	EVENT_REVERT_VALUES,
	EVENT_PAGE_LOADED,
	EVENT_SET_FORM_PRISTINE
} from "constants.js";
import { globs } from "../../../../services/globs";
import { DateTimeFieldAdapter } from "../../../../services/activity-trigger";

export class Controller {
	selection = {};

	constructor($scope, $logger, $timeout, $userProfile, securityService) {
		this._scope = $scope;
		this.opened = false;
		this.$logger = $logger;
		this.$timeout = $timeout;
		this.securityService = securityService;
		this.isReportLoaded = false;
		this.runWhenReportLoadedActions = [];

		const thisUserProfile = $userProfile.getUserProfile();

		if(securityService.hasApplicablePermission(globs.reports.self)) {
			const canEditAction = securityService.isActionAllowed(globs.reports.byId("*").fields.byCode(this.field.Key.Code).edit);
			this.field.isReadonly = !canEditAction || this.field.isReadonly;
		}

		this.isTwelveHour = thisUserProfile.timeDisplay === TIME_DISPLAY_OPTIONS.TWELVE_HOUR;
		this.format = this.isTwelveHour ? "MM/dd/yy hh:mm a" : "MM/dd/yy HH:mm";
		this.externalSet = false;
		this.dateOptions = {
			dateDisabled: false,
			formatYear: "yy",
			startingDay: 0,
			minDate: new Date(12 * 60 * 60 * 1000)
		};
		this.modelOptions = {};

		this.altInputFormats = this.isTwelveHour ?
			[
				"M!-d!-yyyy h:mma", "M!-d!-yy h:mm a", "M!-d!-yy h:mma", "M!-d!-yyyy h:mm a",
				"M!/d!/yyyy h:mma", "M!/d!/yy h:mm a", "M!/d!/yy h:mma", "M!/d!/yyyy h:mm a",
				"M!.d!.yyyy h:mma", "M!.d!.yy h:mm a", "M!.d!.yy h:mma", "M!.d!.yyyy h:mm a",
				"shortDate"
			] : [
				"M!-d!-yyyy H:mm", "M!-d!-yy H:mm", "M!-d!-yy HH:mm", "M!-d!-yyyy HH:mm",
				"M!/d!/yyyy H:mm", "M!/d!/yy H:mm", "M!/d!/yy HH:mm", "M!/d!/yyyy HH:mm",
				"M!.d!.yyyy H:mm", "M!.d!.yy H:mm", "M!.d!.yy HH:mm", "M!.d!.yyyy HH:mm",
				"shortDate"
			];

		const monthNumber = moment().format("MM");
		const year = moment().format("YY");

		this.placeholder = this.isTwelveHour ? `Ex. ${monthNumber}/01/${year} 01:00 PM` : `Ex. ${monthNumber}/01/${year} 13:00`;

		this.maskOptions = {
			maskDefinitions: {
				"9": /\d/,
				"A": /[a-zA-Z]/,
				"*": /[a-zA-Z0-9]/,
				"D": /[AP]/,
				// eslint-disable-next-line no-useless-escape
				"S": /[\d\/\.-:]/,
				"M": /[0-5]/
			},
			clearOnBlur: false,
			clearOnBlurPlaceholder: false
		};

		this.timeMask = this.isTwelveHour ? "9S/9S/99 9S:99 D\\M" : "9S/9S/99 9S:99";
	}

	$onInit() {
		this.capabilities = {
			activityTrigger: new DateTimeFieldAdapter(
				() => !!this.selection.date,
				value => {
					this.externalSet = true;
					this.selection = { date: value };
					this.timepickerDate = this.selection.date;

					this.showTimePicker = false;
					this.suppressNextDateWatch = true;

					this.$timeout(() => this._scope.$apply());
				}
			)
		};
		this._eventBus = this.reportScope.events;
		this._unregister = this.reportFormCtrl.registerFieldController(this);

		if(this.field.MinValue !== undefined) {
			this.dateOptions.minDate = moment()
				.add(this.field.MinValue, "days")
				.toDate();
		}

		if(this.field.MaxValue !== undefined) {
			this.dateOptions.maxDate = moment()
				.add(this.field.MaxValue, "days")
				.toDate();
		}

		const ctrl = this;
		this._scope.$watch(
			() => ctrl.selection?.date,
			(next, prev) => {
				if(next && (prev?.getTime() !== next.getTime())) {
					this.updateFieldDirty();
					this.handleChange();
					if(!this.suppressNextDateWatch) {
						this.externalSet = false;
						this.opened = false;
						ctrl.showTimePicker = true;

						ctrl.$timeout(() => {
							this.timepickerDate = next;
						});
					}
					this.suppressNextDateWatch = false;
				}
			},
			true);

		this._scope.$watch(
			() => ctrl.timepickerDate,
			(next) => {
				if(!!next && this.selection.date) {
					if(next.getTime() !== this.selection.date.getTime()) {
						ctrl.$timeout(() => {
							this.suppressNextDateWatch = true;
							this.selection.date = next;
						});
					}
				}
			},
			true
		);

		this._eventBus.register(EVENT_PAGE_LOADED, () => this.handleReportLoaded(), this._scope);

		this._eventBus.register(EVENT_CLEAR_VALUE(this.field.Id), (e, params) => {
			if(!this.cleared && (!!this.selection?.date) && params.isInitializing !== true) {
				this.cleared = {
					selection: {
						date: this.selection.date
					},
					timepickerDate: this.timepickerDate
				};
			}
			this.selection.date = null;
			this.timepickerDate = null;
		}, this._scope);

		this._eventBus.register(EVENT_UNCLEAR_VALUE(this.field.Id), () => {
			if(!!this.cleared) {
				this.selection = this.cleared.selection;
				this.timepickerDate = this.cleared.timepickerDate;
				this.cleared = undefined;
			}
		}, this._scope);

		this._eventBus.register(EVENT_SET_VALUE(this.field.Id), (e, params) => {
			const currentMoment = moment(this.selection?.date);
			const paramsMoment = moment(params?.value);

			const isWithinAnHour =
				currentMoment.isValid() &&
				paramsMoment.isValid() &&
				Math.abs(currentMoment.diff(paramsMoment, "hours", true)) <= 1;

			if(!this.selection.date || isWithinAnHour) {
				this.externalSet = true;
				this.selection = { date: params.value };
				this.timepickerDate = this.selection.date;

				this.$timeout(() => this._scope.$apply());
			}
		}, this._scope);

		this._eventBus.register(EVENT_SET_DEFAULT_VALUE(this.field.Id), () => {
			this.externalSet = true;
			this.selection = { date: new Date() };
			this.timepickerDate = this.selection.date;

			this.showTimePicker = false;
			this.suppressNextDateWatch = true;

			this.$timeout(() => this._scope.$apply());
		}, this._scope);

		this._eventBus.register(EVENT_REVERT_VALUES, () => {
			this.revertValue();
		}, this._scope);

		this._eventBus.register(EVENT_SET_FORM_PRISTINE, () => {
			this.setPristine();
		}, this._scope);

		this.setValue();
	}

	$onDestroy() {
		this._unregister();
	}

	handleClickOutside() {
		this.showTimePicker = false;
	}

	openTimepicker() {
		if(this.selection && !this.selection.date) {
			const initializedDate = new Date();
			initializedDate.setHours(0);
			initializedDate.setMinutes(0);
			initializedDate.setSeconds(0);
			initializedDate.setMilliseconds(0);
			this.selection.date = initializedDate;
		}

		this.timepickerDate = this.selection.date;

		this.showTimePicker = true;
	}

	getCurrentValue(isApiCall = false) {
		let result = this.buildResult();

		if(!isApiCall && this.field.IsNotReported !== true && this.field.IsHidden !== true) {
			if(!result) {
				result = { FieldId: this.field.Id };
				result.hasResultForRuleRun = false;
			}

			result.isDirty = (result.Date && moment(result.Date).format()) != (this.initialValue && moment(this.initialValue.Date).format());
		}

		return result ? [result] : [];
	}

	revertValue() {
		if(this.initialValue) {
			const localShiftedMoment = this.initialValue && moment(this.initialValue.Date, "YYYY-MM-DDThh:mm:ss");

			const date = localShiftedMoment.toDate(); //this value is now clean for the field

			this.selection.date = date;
		}
		else {
			this.selection.date = undefined;
		}
	}

	buildResult() {
		const timezone = this.field.timezone ?? this.reportScope.form.TimeZone.Code;

		const outgoingDate = moment(this.selection.date).tz(timezone, true);

		return this.selection.date
			? {
				FieldId: this.field.Id,
				Date: outgoingDate.format("YYYY-MM-DDTHH:mm:00Z")
			} : undefined;
	}

	setPristine() {
		this.initialValue = this.buildResult();
	}

	handleChange() {
		this.runWhenReportLoaded(() => {
			if(this.selection.date && !this.field.isReadonly && !this.field.isDisabled) {
				if(this.field.IsPrimary) {
					const asOf = moment(this.selection.date).tz(this.field.timezone, true);
					this._eventBus.trigger(EVENT_FILTER_PRIMARYDATE, {
						effectiveAsOf: { fromInclusive: asOf, toExclusive: asOf, allowPartial: true },
						autoSelect: true
					});
				}
			}

			this.runWhenTimezoneReady(() => {
				const selectionMoment = moment(this.selection.date);
				const timezoneShifted = selectionMoment.tz(this.field.timezone, true);
				const selectionDateString = timezoneShifted.toDate();

				this._eventBus.trigger(EVENT_VALUE_CHANGED(this.field.Id), {
					field: this.field,
					value: selectionDateString
				});
			});
		});
	}

	handleReportLoaded() {
		this.initialValue = find(this.values, v => v.FieldId === this.field.Id);

		if(!!this.initialValue) {
			const setInitialValue = () => {
				const localShiftedMoment = moment(this.initialValue.Date, "YYYY-MM-DDThh:mm:ss");
				this.initialValue.Date = moment(localShiftedMoment.toDate()).tz(this.field.timezone, true)
					.format("YYYY-MM-DDTHH:mm:00Z");
			};

			if((this.field.needsTimezone === true) && (this.field.timezone === undefined)) {
				const unregisterTz = this._scope.$watch(() => this.field.timezone, (next) => {
					if(next !== undefined) {
						setInitialValue();
						unregisterTz();
					}
				});
			}
			else {
				setInitialValue();
			}
		}

		this.isReportLoaded = true;
		this.runWhenReportLoadedActions.forEach(action => action());
		this.runWhenReportLoadedActions = [];
	}

	runWhenReportLoaded(action) {
		if(this.isReportLoaded) {
			action();
			return;
		}

		this.runWhenReportLoadedActions.push(action);
	}

	runWhenTimezoneReady(callback) {
		if(this.field.timezone) {
			callback();
		}
		else {
			const unregister = this._scope.$watch(() => this.field.timezone, (next) => {
				if(next) {
					unregister();
					callback();
				}
			});
		}
	}

	updateFieldDirty() {
		let outgoingDate = moment(this.selection.date);

		if(this.field.timezone) {
			outgoingDate = outgoingDate.tz(this.field.timezone, true);
		}
		const outgoingDateStr = outgoingDate.format("YYYY-MM-DDTHH:mm:00Z");

		const isDirty = (this.field.visible !== false || this.initialValue !== undefined) &&
			((outgoingDateStr && moment(outgoingDateStr).format()) != (this.initialValue && moment(this.initialValue.Date).format()));

		this.setFieldDirty(this.field.Id, isDirty);
	}

	setValue() {
		this.value = find(this.values, v => v.FieldId === this.field.Id);

		if(this.value) {
			const localShiftedMoment = moment(this.value.Date, "YYYY-MM-DDThh:mm:ss");

			this.selection.date = localShiftedMoment.toDate();

			this.handleChange();
			this.timepickerDate = this.selection.date;
		}
	}

	open() {
		this.opened = true;
	}

	getValidationStyling() {
		return this.field.hasValidationError ? "validation-error"
			: this.field.hasValidationWarning ? "validation-warning" : "";
	}
}

export default angular
	.module("vesl.components.report-form.date-time", [])
	.component("dateTime", {
		template,
		controller: Controller,
		bindings: {
			field: "<",
			values: "<",
			setFieldDirty: "="
		},
		require: {
			reportFormCtrl: "^reportForm",
			reportScope: "^^reportScope"
		}
	}).name;
